/**
 * WorkspacePaperworkRow — which paperwork a deal uses, at a glance.
 *
 * Reads the negotiation payload's paperwork fields (design §8.4):
 * `paperwork_category`, `seller_disclosure_title` (already carrying any
 * " (Voluntary)" suffix), `condo_documents_applies`, and the deal's
 * `transaction_id` once an offer is accepted. Renders nothing when the payload
 * has none of them, so workspaces from before the paperwork release look
 * exactly as they did.
 */
import React from 'react';
import { CATEGORY_LABELS, WI_PAPERWORK_SPECS } from '../SellerDashboard/paperworkPresentation';
import styles from './WorkspacePaperworkRow.module.css';

/** "Wisconsin Condominium" in Wisconsin, "Condominium" elsewhere, or null. */
export function workspacePaperworkLabel(workspace) {
  const category = workspace?.paperwork_category;
  if (!category) return null;
  const state = String(workspace?.property?.state || '').toUpperCase();
  if (state === 'WI' && WI_PAPERWORK_SPECS[category]) return WI_PAPERWORK_SPECS[category].label;
  return CATEGORY_LABELS[category] || null;
}

export default function WorkspacePaperworkRow({ workspace }) {
  const paperwork = workspacePaperworkLabel(workspace);
  const rawTitle = workspace?.seller_disclosure_title;
  const disclosure = typeof rawTitle === 'string' && rawTitle.trim() ? rawTitle.trim() : null;
  const transactionId = workspace?.transaction_id ?? null;
  // The § 703.33 condominium document package applies to this deal; its
  // delivery and review clock live on the transaction (design §9.6).
  const condoDocuments = workspace?.condo_documents_applies === true;

  if (!paperwork && !disclosure && transactionId == null && !condoDocuments) return null;

  return (
    <dl className={styles.row} data-testid="workspace-paperwork-row" aria-label="Deal paperwork">
      {paperwork && (
        <div className={styles.item}>
          <dt className={styles.term}>Paperwork</dt>
          <dd className={styles.value} data-testid="workspace-paperwork-category">
            {paperwork}
          </dd>
        </div>
      )}
      {disclosure && (
        <div className={styles.item}>
          <dt className={styles.term}>Seller disclosure</dt>
          <dd className={styles.value} data-testid="workspace-paperwork-disclosure">
            {disclosure}
          </dd>
        </div>
      )}
      {condoDocuments && (
        <div className={styles.item}>
          <dt className={styles.term}>Condominium documents</dt>
          <dd className={styles.value} data-testid="workspace-paperwork-condo-documents">
            {transactionId != null ? 'Tracked in the transaction' : 'Tracked once an offer is accepted'}
          </dd>
        </div>
      )}
      {transactionId != null && (
        <div className={styles.item}>
          <dt className={styles.term}>Transaction</dt>
          <dd className={`${styles.value} ${styles.tag}`} data-testid="workspace-paperwork-transaction">
            T#{transactionId}
          </dd>
        </div>
      )}
    </dl>
  );
}
